import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import { useTheme, Paper, Box, Typography, Chip, IconButton } from '@mui/material';
import { useIntl } from 'react-intl';

import { AnnotatorAnnotationCard } from './AnnotatorAnnotationCard';
import { useAnnotatorConceptCard } from './useAnnotatorConceptCard';
import { getStatusConfig } from '../../../../../annotation/components/AnnotationConfig/AnnotationCard';
import type { Annotation } from '../../../../../workspace-common/types/responseTypes';
import { AnnotatorEvidencesList } from '../EditAndAddAnnotations/AnnotatorEvidencesList';
import { ReferenceTextSection } from '../ReferenceTextSection';

interface AnnotatorConceptCardProps {
  conceptId: string;
  conceptName: string;
  annotations: Annotation[];
}

export const AnnotatorConceptCard = ({
  conceptId,
  conceptName,
  annotations,
}: AnnotatorConceptCardProps) => {
  const theme = useTheme();
  const { formatMessage } = useIntl();
  const { isExpanded, handleSetIsExpanded } = useAnnotatorConceptCard({ conceptId });

  return (
    <Paper
      elevation={2}
      sx={{
        borderRadius: 0,
        paddingX: theme.spacing(4),
        paddingY: theme.spacing(2),
        display: 'flex',
        flexDirection: 'column',
        border: `1px solid ${theme.palette.grey[300]}`,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%',
        }}
      >
        <Typography variant="body1">{conceptName}</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: theme.spacing(1) }}>
          <Chip
            variant="filled"
            sx={{
              backgroundColor: theme.palette.grey[200],
              size: 'small',
              fontSize: '13px',
            }}
            label={annotations.length}
          />

          {isExpanded ? (
            <IconButton onClick={() => handleSetIsExpanded(false)}>
              <KeyboardArrowUp />
            </IconButton>
          ) : (
            <IconButton onClick={() => handleSetIsExpanded(true)}>
              <KeyboardArrowDown />
            </IconButton>
          )}
        </Box>
      </Box>

      {isExpanded && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(3) }}>
          {annotations.map((annotation, index: number) => (
            <Box key={annotation.annotationId ?? index}>
              <Paper
                sx={{
                  borderRadius: 0,
                  backgroundColor: getStatusConfig(annotation.status, theme).bgColor,
                  border: `1px solid ${getStatusConfig(annotation.status, theme).borderColor}`,
                  padding: theme.spacing(4),
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: theme.spacing(2),
                    mb: theme.spacing(2),
                  }}
                >
                  {getStatusConfig(annotation.status, theme).icon}
                  <Typography variant="body1">Annotation #{index + 1}</Typography>
                  <Chip
                    size="small"
                    label={formatMessage({
                      id: getStatusConfig(annotation.status, theme).labelKey,
                    })}
                    sx={{
                      backgroundColor: getStatusConfig(annotation.status, theme).chipBg,
                      color: getStatusConfig(annotation.status, theme).chipLabelColor,
                      fontSize: '12px',
                    }}
                  />
                  {annotation.spans.length === 0 && (
                    <ReferenceTextSection referenceText={annotation.referenceText} />
                  )}
                </Box>
                <AnnotatorAnnotationCard annotation={annotation} />
                <AnnotatorEvidencesList
                  annotation={annotation}
                  conceptId={conceptId}
                  editingAnnotationId={annotation.annotationId}
                />
              </Paper>
            </Box>
          ))}
        </Box>
      )}
    </Paper>
  );
};
